import { FC, useMemo, useState } from "react";
import { MDBBtn } from "mdb-react-ui-kit";
import { QuillDeltaToHtmlConverter } from "quill-delta-to-html";
import Image from "next/image";
import dynamic from "next/dynamic";
import { TourReadableTO } from "../../models/detail";
import styles from "../../styles/detail/DetailDescription.module.scss";
import PackageDetailDescriptionCarousel from "./PackageDetailDescriptionCarousel";
import { ActivityReableTO } from "../../models/common/common";
import { useShouldRenderArrayFC } from "../../utils/hooks/useShouldRenderArrayFC";
import generalSvg from "../../utils/svg-icon/general";
import TourReviewDetail from "./TourReviewDetail";
import ActivityIconBody from "./DetailBooking/DetailBookingSidebar/ActivityIconBody";

const PhotoGalleryModal = dynamic(() => import("./PhotoGalleryModal"), {
    ssr: false,
});

/* eslint-disable no-underscore-dangle */
const PackageDetailDescription: FC<{
    packageDetail: TourReadableTO;
    gallery?: string[];
    whatIsIncluded?: TourReadableTO["whatIsIncluded"];
    whatIsIncludedDelta?: TourReadableTO["whatIsIncludedDelta"];
}> = ({ packageDetail, gallery = [], whatIsIncluded, whatIsIncludedDelta }) => {
    const [imageErrors, setImageErrors] = useState<Set<number>>(new Set());
    const [showModal, setShowModal] = useState(false);
    const [indexPhotoClicked, setIndexPhotoClicked] = useState(0);
    const defaultImage = "/images/tours/across-italy-reduced.jpg";
    const { ImageLine } = generalSvg;

    const openModal = (index: number) => {
        setIndexPhotoClicked(index);
        setShowModal(true);
    };
    const uniq = (arr: ActivityReableTO[], track = new Set()) =>
        arr.filter(({ name }) => (track.has(name) ? false : track.add(name)));

    const activities = useMemo(() => {
        const results = packageDetail.itineraries.map(
            el => el.itinerary.activities
        );
        return uniq(results.flat());
    }, [packageDetail]);

    const converter = new QuillDeltaToHtmlConverter(
        packageDetail.whatWillYouSeeDelta?.ops ?? []
    );

    const html = converter.convert();

    return (
        <div>
            <div className={styles.container}>
                <div className={styles.textContainer}>
                    <h2>
                        What will <span>you see?</span>
                    </h2>
                    {packageDetail.whatWillYouSeeDelta?.ops ? (
                        <div dangerouslySetInnerHTML={{ __html: html }} />
                    ) : (
                        <p>{packageDetail.whatWillYouSee}</p>
                    )}
                </div>
                {gallery && gallery.length > 0 && (
                    <div className={styles.galleryContainer}>
                        <div className={styles.galleryLayout}>
                            {gallery[0] && (
                                <div className={styles.firstImageContainer}>
                                    <div
                                        className={styles.firstImageWrapper}
                                        onClick={() => openModal(0)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <Image
                                            src={
                                                imageErrors.has(0)
                                                    ? defaultImage
                                                    : gallery[0]
                                            }
                                            alt="Gallery image 1"
                                            fill
                                            className={styles.firstImage}
                                            onError={() => {
                                                setImageErrors(prev =>
                                                    new Set(prev).add(0)
                                                );
                                            }}
                                        />
                                        <MDBBtn
                                            className={
                                                styles.showAllPhotosButton
                                            }
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal(0);
                                            }}
                                        >
                                            <ImageLine className="align-self-center me-2" />
                                            Show all photos
                                        </MDBBtn>
                                    </div>
                                </div>
                            )}
                            {gallery.length > 1 && (
                                <div className={styles.smallImagesGrid}>
                                    {gallery
                                        .slice(1, 5)
                                        .map((imageUrl, index) => (
                                            <div
                                                key={imageUrl}
                                                className={
                                                    styles.smallImageWrapper
                                                }
                                                onClick={() =>
                                                    openModal(index + 1)
                                                }
                                                style={{ cursor: "pointer" }}
                                            >
                                                <Image
                                                    src={
                                                        imageErrors.has(
                                                            index + 1
                                                        )
                                                            ? defaultImage
                                                            : imageUrl
                                                    }
                                                    alt={`Gallery image ${
                                                        index + 2
                                                    }`}
                                                    fill
                                                    className={
                                                        styles.smallImage
                                                    }
                                                    onError={() => {
                                                        setImageErrors(prev =>
                                                            new Set(prev).add(
                                                                index + 1
                                                            )
                                                        );
                                                    }}
                                                />
                                            </div>
                                        ))}
                                </div>
                            )}
                        </div>
                        <p className={styles.galleryDescription}>
                            Want a sneak peak? Here are some of our shootings.{" "}
                            <strong>
                                A magical adventure in Italy awaits you!
                            </strong>
                        </p>
                    </div>
                )}
                {whatIsIncluded && whatIsIncluded.length > 0 && (
                    <div style={{ marginTop: "2rem", marginBottom: "2rem" }}>
                        <ActivityIconBody
                            whatIsIncluded={whatIsIncluded}
                            whatIsIncludedDelta={whatIsIncludedDelta ?? []}
                            activities={activities}
                        />
                    </div>
                )}
            </div>

            {useShouldRenderArrayFC(packageDetail.reviews) && (
                <TourReviewDetail reviewContents={packageDetail.reviews!} />
            )}

            {useShouldRenderArrayFC(packageDetail.italiansExpert) && (
                <PackageDetailDescriptionCarousel
                    italiansExpert={packageDetail.italiansExpert!}
                />
            )}
            {showModal && gallery && gallery.length > 0 && (
                <PhotoGalleryModal
                    photoUrls={gallery}
                    onClose={() => setShowModal(false)}
                    indexPhotoClicked={indexPhotoClicked}
                />
            )}
        </div>
    );
};

export default PackageDetailDescription;
